import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Edit, Trash2, Calendar, Eye, FileText } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { BlogPost } from "@shared/blog-schema";
import SuperAdminLayout from "@/components/admin/super-admin-layout";
import { SimpleRichTextEditor } from "@/components/ui/simple-rich-text-editor";

const blogPostSchema = z.object({
  title: z.string().min(1, "Title is required"),
  slug: z.string().min(1, "Slug is required"),
  excerpt: z.string().min(1, "Excerpt is required"),
  content: z.string().min(1, "Content is required"),
  authorName: z.string().min(1, "Author name is required"),
  authorEmail: z.string().email("Valid email is required"),
  featuredImage: z.string().optional(),
  tags: z.array(z.string()).optional(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  isPublished: z.boolean(),
});

type BlogPostFormValues = z.infer<typeof blogPostSchema>;

export default function BlogManagementPage() {
  return (
    <SuperAdminLayout>
      <BlogManagementContent />
    </SuperAdminLayout>
  );
}

function BlogManagementContent() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [tagsInput, setTagsInput] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: blogPosts = [], isLoading } = useQuery({
    queryKey: ['/api/admin/blog/posts'],
    select: (data: BlogPost[]) => data
  });

  const form = useForm<BlogPostFormValues>({
    resolver: zodResolver(blogPostSchema),
    defaultValues: {
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      authorName: "",
      authorEmail: "",
      featuredImage: "",
      tags: [],
      metaTitle: "",
      metaDescription: "",
      isPublished: false,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: BlogPostFormValues) => {
      console.log("About to send API request with data:", data);
      const response = await apiRequest("/api/admin/blog/posts", {
        method: "POST",
        body: data
      });
      console.log("API response:", response);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/blog/posts'] });
      setIsCreateDialogOpen(false);
      form.reset();
      setTagsInput("");
      toast({
        title: "Success",
        description: "Blog post created successfully",
      });
    },
    onError: (error) => {
      console.error("Create blog post error:", error);
      toast({
        title: "Error",
        description: error?.details || "Failed to create blog post",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number, data: BlogPostFormValues }) => 
      apiRequest(`/api/admin/blog/posts/${id}`, {
        method: "PUT",
        body: data
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/blog/posts'] });
      setEditingPost(null);
      form.reset();
      setTagsInput("");
      toast({
        title: "Success",
        description: "Blog post updated successfully",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => 
      apiRequest(`/api/admin/blog/posts/${id}`, {
        method: "DELETE"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/blog/posts'] });
      toast({
        title: "Success",
        description: "Blog post deleted successfully",
      });
    },
  });

  const handleSubmit = (values: BlogPostFormValues) => {
    const tags = tagsInput.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    const postData = { ...values, tags };
    
    console.log("Form submission data:", JSON.stringify(postData, null, 2));

    if (editingPost) {
      updateMutation.mutate({ id: editingPost.id, data: postData });
    } else {
      console.log("Calling createMutation with:", postData);
      createMutation.mutate(postData);
    }
  };

  const openEditDialog = (post: BlogPost) => {
    setEditingPost(post);
    form.reset({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      authorName: post.authorName,
      authorEmail: post.authorEmail,
      featuredImage: post.featuredImage || "",
      metaTitle: post.metaTitle || "",
      metaDescription: post.metaDescription || "",
      isPublished: post.isPublished,
    });
    setTagsInput(post.tags?.join(', ') || "");
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Blog Management</h1>
          <p className="text-gray-600 mt-1">Create and manage blog posts for SAASGAMA</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Blog Post</DialogTitle>
              <DialogDescription>
                Create a new blog post for the SAASGAMA website
              </DialogDescription>
            </DialogHeader>
            <BlogPostForm 
              form={form}
              tagsInput={tagsInput}
              setTagsInput={setTagsInput}
              onSubmit={handleSubmit}
              onCancel={() => {
                setIsCreateDialogOpen(false);
                form.reset();
                setTagsInput("");
              }}
              isSubmitting={createMutation.isPending}
              generateSlug={generateSlug}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Blog Posts List */}
      {isLoading ? (
        <div className="grid gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : blogPosts.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No blog posts yet</h3>
            <p className="text-gray-600">Create your first blog post to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {blogPosts.map((post) => (
            <Card key={post.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-xl">{post.title}</CardTitle>
                      <Badge variant={post.isPublished ? "default" : "secondary"}>
                        {post.isPublished ? "Published" : "Draft"}
                      </Badge>
                    </div>
                    <CardDescription className="text-sm text-gray-600">
                      {post.excerpt}
                    </CardDescription>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.createdAt)}
                      </div>
                      <div>By {post.authorName}</div>
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex gap-1">
                          {post.tags.slice(0, 3).map((tag, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {post.isPublished && (
                      <Button variant="ghost" size="sm" onClick={() => window.open(`/blog/${post.slug}`, '_blank')}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(post)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this blog post?')) {
                          deleteMutation.mutate(post.id);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingPost} onOpenChange={(open) => !open && setEditingPost(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Blog Post</DialogTitle>
            <DialogDescription>
              Update the blog post details
            </DialogDescription>
          </DialogHeader>
          {editingPost && (
            <BlogPostForm 
              form={form}
              tagsInput={tagsInput}
              setTagsInput={setTagsInput}
              onSubmit={handleSubmit}
              onCancel={() => {
                setEditingPost(null);
                form.reset();
                setTagsInput("");
              }}
              isSubmitting={updateMutation.isPending}
              generateSlug={generateSlug}
              isEditing={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Blog Post Form Component
function BlogPostForm({ 
  form, 
  tagsInput, 
  setTagsInput, 
  onSubmit, 
  onCancel, 
  isSubmitting, 
  generateSlug,
  isEditing = false 
}: {
  form: any;
  tagsInput: string;
  setTagsInput: (value: string) => void;
  onSubmit: (values: BlogPostFormValues) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  generateSlug: (title: string) => string;
  isEditing?: boolean;
}) {
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter blog post title" 
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      if (!isEditing && !form.getValues('slug')) {
                        form.setValue('slug', generateSlug(e.target.value));
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Slug</FormLabel>
                <FormControl>
                  <Input placeholder="blog-post-url" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="excerpt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Excerpt</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Brief description of the blog post"
                  className="min-h-[80px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Content</FormLabel>
              <FormControl>
                <SimpleRichTextEditor
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Write your blog post content here..."
                  className="min-h-[300px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="authorName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Author Name</FormLabel>
                <FormControl>
                  <Input placeholder="Author name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="authorEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Author Email</FormLabel>
                <FormControl>
                  <Input placeholder="author@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="featuredImage"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Featured Image</FormLabel>
              <div className="space-y-4">
                {/* Current image preview */}
                {field.value && (
                  <div className="relative">
                    <img 
                      src={field.value} 
                      alt="Featured image preview" 
                      className="w-full h-48 object-cover rounded-lg border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => field.onChange("")}
                    >
                      Remove
                    </Button>
                  </div>
                )}
                
                {/* File upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          const formData = new FormData();
                          formData.append('image', file);
                          
                          console.log('Uploading file:', file.name, file.size);
                          
                          const response = await fetch('/api/upload/blog-image', {
                            method: 'POST',
                            body: formData
                          });
                          
                          console.log('Upload response status:', response.status);
                          
                          if (response.ok) {
                            const result = await response.json();
                            console.log('Upload result:', result);
                            field.onChange(result.url);
                            // Show success feedback
                            console.log('Image uploaded successfully');
                          } else {
                            const errorText = await response.text();
                            console.error('Upload failed:', response.status, errorText);
                            // Show error feedback
                            alert(`Upload failed: ${response.status}. Please try again.`);
                            throw new Error(`Upload failed: ${response.status}`);
                          }
                        } catch (error) {
                          console.error('Upload error:', error);
                        }
                      }
                    }}
                    className="hidden"
                    id="featured-image-upload"
                  />
                  <label
                    htmlFor="featured-image-upload"
                    className="cursor-pointer"
                  >
                    <div className="space-y-2">
                      <div className="text-2xl">📁</div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium text-blue-600 hover:text-blue-500">
                          Click to upload
                        </span> or drag and drop
                      </div>
                      <div className="text-xs text-gray-500">
                        Recommended: 1200×630 pixels (16:9 ratio)
                        <br />
                        PNG, JPG, WebP up to 2MB
                      </div>
                    </div>
                  </label>
                </div>
                
                {/* Manual URL input as fallback */}
                <div className="text-sm">
                  <details className="cursor-pointer">
                    <summary className="text-gray-500 hover:text-gray-700">
                      Or enter image URL manually
                    </summary>
                    <div className="mt-2">
                      <Input 
                        placeholder="https://example.com/image.jpg" 
                        value={field.value || ""}
                        onChange={field.onChange}
                      />
                    </div>
                  </details>
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div>
          <label className="text-sm font-medium">Tags</label>
          <Input
            placeholder="Enter tags separated by commas"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            className="mt-1"
          />
        </div>

        <div className="rounded-lg border border-dashed border-blue-200 bg-blue-50/40 p-4 space-y-4">
          <p className="text-sm font-semibold text-blue-700">SEO Meta Tags</p>
          <FormField
            control={form.control}
            name="metaTitle"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Meta Title <span className="text-gray-400 font-normal">(50–60 chars recommended)</span></FormLabel>
                <FormControl>
                  <Input placeholder="SEO title shown in search results" {...field} />
                </FormControl>
                <div className="text-xs text-gray-400 text-right">{field.value?.length || 0} chars</div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="metaDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Meta Description <span className="text-gray-400 font-normal">(150–160 chars recommended)</span></FormLabel>
                <FormControl>
                  <Textarea placeholder="SEO description shown in search results" className="min-h-[80px]" {...field} />
                </FormControl>
                <div className="text-xs text-gray-400 text-right">{field.value?.length || 0} chars</div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="isPublished"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Publish Post</FormLabel>
                <div className="text-sm text-gray-600">
                  Make this post visible on the website
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : isEditing ? "Update Post" : "Create Post"}
          </Button>
        </div>
      </form>
    </Form>
  );
}